import { Pipe, PipeTransform } from '@angular/core';
import { Observable } from 'rxjs';
import Prism from 'prismjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TabSdk } from '@techextensor/tab-sdk';
import { FtpImageLoaderUtils } from '../utils/ftp-image-loader.utils';

@Pipe({
  name: 'contentPreview',
  standalone: true,
})
export class ContentPreviewPipe implements PipeTransform {
  constructor(
    private sanitizer: DomSanitizer,
  ) { }

  transform(rawInput: string): Observable<SafeHtml> {
    return new Observable<SafeHtml>(subscriber => {
      if (!rawInput?.length) {
        subscriber.next('');
        subscriber.complete();
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(rawInput, 'text/html');

      // Add spinner placeholders for FTP images
      const ftpImages = FtpImageLoaderUtils.addPlaceholders(doc);

      // Process expandable elements and syntax highlighting
      this.processExpandableElements(doc);
      Prism.highlightAllUnder(doc.body);

      // Emit immediately with placeholders (or final if no FTP images)
      subscriber.next(this.buildSafeHtml(doc));

      if (ftpImages.length === 0) {
        subscriber.complete();
        return;
      }

      let cancelled = false;

      // Fetch all images in parallel using data URLs (survive innerHTML serialization),
      // then emit once with all images resolved
      this.fetchAllImages(doc, ftpImages, () => cancelled)
        .then(() => {
          if (!cancelled) {
            subscriber.next(this.buildSafeHtml(doc));
          }
          subscriber.complete();
        });

      // Teardown: stop processing on unsubscribe
      return () => {
        cancelled = true;
      };
    });
  }

  private buildSafeHtml(doc: Document): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      `${doc.head.innerHTML}${doc.body.innerHTML}`
    );
  }

  private async fetchAllImages(
    doc: Document,
    ftpImages: any[],
    isCancelled: () => boolean,
  ): Promise<void> {
    const fetchPromises = ftpImages.map(async ({ id, src, useDefaultHeaders }) => {
      try {
        if (isCancelled()) return;

        // Use data URLs (asDataUrl=true) — blob URLs don't survive innerHTML re-render
        const dataUrl = await FtpImageLoaderUtils.fetchImage(src, useDefaultHeaders, true);

        if (!isCancelled()) {
          FtpImageLoaderUtils.replacePlaceholder(doc, id, dataUrl);
        }
      } catch (err) {
        console.error('Image processing failed:', err);
      }
    });

    await Promise.allSettled(fetchPromises);
  }

  private processExpandableElements(doc: Document) {
    const expandableElements = doc.querySelectorAll('[data-tab-expandable]');
    if (expandableElements.length > 0) {
      expandableElements.forEach(element => {
        const attrValue = element.getAttribute('data-tab-expandable');
        const targetSelector = attrValue && attrValue.trim() ? attrValue : undefined;
        TabSdk.util.processTabExpandable(element, targetSelector, doc);
      });
    }
  }
}